import { Popover } from 'bootstrap';
import { errorSwal } from './plugins/sweetalert2.js';
import { debounce, post } from './helper.js';

const FORM_SELECTOR = '[data-password-validation]';
const RULE_SELECTOR = '[data-password-rule]';
const PASSWORD_SELECTOR = '[data-password-input="password"]';
const CONFIRMATION_SELECTOR = '[data-password-input="confirmation"]';
const SUBMIT_SELECTOR = '[data-password-submit]';
const STATUS_SELECTOR = '[data-password-validation-status]';
const FOCUS_REGION_SELECTOR = '[data-password-focus-region]';
const POPOVER_CONTENT_SELECTOR = '[data-password-popover-content]';
const POPOVER_TOGGLE_SELECTOR = '[data-bs-toggle="popover"]';
const GENERATE_SELECTOR = '[data-password-generate]';
const STATE = Symbol('passwordValidationState');
const CHECK_UNAVAILABLE_MESSAGE = 'De live controle is tijdelijk niet beschikbaar. Bij opslaan wordt het wachtwoord opnieuw gecontroleerd.';

const getForms = container => {
    if (!container) {
        return [];
    }

    if (container.matches?.(FORM_SELECTOR)) {
        return [container];
    }

    return Array.from(container.querySelectorAll(FORM_SELECTOR));
};

const setIconVisibility = (ruleElement, isValid) => {
    const successIcon = ruleElement.querySelector('.fa-check');
    const errorIcon = ruleElement.querySelector('.fa-times');

    if (successIcon) {
        successIcon.style.display = isValid ? 'inline-block' : 'none';
    }

    if (errorIcon) {
        errorIcon.style.display = isValid ? 'none' : 'inline-block';
    }

    ruleElement.classList.toggle('requirements__item--valid', isValid === true);
};

const setStatus = (state, message = '') => {
    if (!state.statusElement) {
        return;
    }

    state.statusElement.textContent = message;
    state.statusElement.hidden = message === '';
};

const setLoading = (state, loading) => {
    state.popoverContent?.classList.toggle('password-requirements-panel--checking', loading);
    state.popoverContent?.setAttribute('aria-busy', loading ? 'true' : 'false');
};

const setSubmitDisabled = (state, disabled) => {
    if (!state.advisory && state.submitButton) {
        state.submitButton.disabled = disabled;
    }
};

const renderEmpty = state => {
    state.ruleElements.forEach(ruleElement => setIconVisibility(ruleElement, false));
    setStatus(state);
    setLoading(state, false);
    setSubmitDisabled(state, false);
};

const renderRules = (state, rules) => {
    let hasInvalidRule = false;

    state.ruleElements.forEach(ruleElement => {
        const key = ruleElement.dataset.passwordRule;
        const isValid = Boolean(rules[key]);

        if (!isValid) {
            hasInvalidRule = true;
        }

        setIconVisibility(ruleElement, isValid);
    });

    setStatus(state);
    setLoading(state, false);
    setSubmitDisabled(state, hasInvalidRule);
};

const getLocalRules = state => {
    const rules = {};

    if (state.ruleElements.some(rule => rule.dataset.passwordRule === 'match')) {
        rules.match = Boolean(
            state.passwordInput
            && state.confirmationInput
            && state.passwordInput.value.length > 0
            && state.passwordInput.value === state.confirmationInput.value
        );
    }

    return rules;
};

const initializePopover = state => {
    if (!state.passwordInput.matches(POPOVER_TOGGLE_SELECTOR)) {
        return;
    }

    const focusRegion = state.form.querySelector(FOCUS_REGION_SELECTOR);
    const content = state.form.querySelector(POPOVER_CONTENT_SELECTOR);

    if (!focusRegion || !content) {
        return;
    }

    content.classList.add('password-requirements-panel--popover');

    const popover = new Popover(state.passwordInput, {
        animation: false,
        container: document.body,
        content,
        customClass: 'password-requirements-popover',
        fallbackPlacements: ['top', 'right', 'left'],
        html: true,
        sanitize: false,
    });

    const show = () => {
        if (!state.passwordInput.isConnected) {
            return;
        }

        popover.show();
        state.passwordInput.setAttribute('aria-expanded', 'true');
    };

    const hide = () => {
        popover.hide();
        state.passwordInput.setAttribute('aria-expanded', 'false');
    };

    state.passwordInput.setAttribute('aria-expanded', 'false');
    state.focusRegion = focusRegion;
    state.popover = popover;
    state.showPopover = show;
    state.hidePopover = hide;

    const listenerOptions = {
        signal: state.eventController.signal,
    };

    focusRegion.addEventListener('focusin', show, listenerOptions);
    focusRegion.addEventListener('click', event => {
        if (event.target.closest(GENERATE_SELECTOR)) {
            return;
        }

        window.setTimeout(() => {
            if (focusRegion.contains(document.activeElement)) {
                hide();
                show();
            }
        }, 0);
    }, listenerOptions);
    focusRegion.addEventListener('focusout', () => {
        window.setTimeout(() => {
            if (!focusRegion.contains(document.activeElement)) {
                hide();
            }
        }, 0);
    }, listenerOptions);

    state.form.addEventListener('keydown', event => {
        if (event.key === 'Escape') {
            hide();
        }
    }, listenerOptions);

    focusRegion.querySelector(GENERATE_SELECTOR)?.addEventListener('click', hide, listenerOptions);
};

const disposePasswordValidation = state => {
    state.eventController.abort();
    state.popover?.dispose();

    if (state.form[STATE] === state) {
        delete state.form[STATE];
        delete state.form.dataset.passwordValidationBound;
    }
};

const updatePasswordValidation = async form => {
    const state = form?.[STATE];

    if (!state?.passwordInput) {
        return;
    }

    const passwordIsEmpty = state.passwordInput.value.length === 0;

    if (state.optional && passwordIsEmpty) {
        form.dataset.passwordValidationRequest = String(Number(form.dataset.passwordValidationRequest || '0') + 1);
        renderEmpty(state);

        return;
    }

    const requestUrl = form.dataset.passwordCheckUrl;
    let rules = getLocalRules(state);

    if (requestUrl) {
        form.dataset.passwordValidationRequest = String(Number(form.dataset.passwordValidationRequest || '0') + 1);
        const requestId = form.dataset.passwordValidationRequest;

        if (form.dataset.passwordValidationError === 'inline') {
            setSubmitDisabled(state, true);
        }
        setStatus(state);
        setLoading(state, true);

        try {
            const response = await post(requestUrl, new FormData(form));

            if (form.dataset.passwordValidationRequest !== requestId) {
                return;
            }

            setLoading(state, false);
            rules = {
                ...response.rules,
                ...rules,
            };
        } catch (error) {
            console.error('[PasswordValidation] Error checking password:', error);

            if (form.dataset.passwordValidationRequest !== requestId) {
                return;
            }

            setLoading(state, false);
            // The submit validates the password again, so a failed live check must not block saving
            setSubmitDisabled(state, false);

            if (form.dataset.passwordValidationError === 'inline') {
                setStatus(state, CHECK_UNAVAILABLE_MESSAGE);

                return;
            }

            // Show the popup once per form, not once for every failed request while typing
            if (!state.checkErrorShown) {
                state.checkErrorShown = true;

                errorSwal.fire({
                    title: 'Fout bij controleren wachtwoord',
                    text: CHECK_UNAVAILABLE_MESSAGE,
                    icon: 'error'
                });
            }

            return;
        }
    }

    renderRules(state, rules);
};

const bindPasswordValidation = form => {
    const passwordInput = form.querySelector(PASSWORD_SELECTOR);

    if (
        form.dataset.passwordValidationBound === '1'
        && form[STATE]
        && form[STATE].passwordInput === passwordInput
        && passwordInput?.isConnected
    ) {
        return form[STATE];
    }

    if (form[STATE]) {
        disposePasswordValidation(form[STATE]);
    }

    const confirmationInput = form.querySelector(CONFIRMATION_SELECTOR);
    const submitButton = form.querySelector(SUBMIT_SELECTOR);
    const ruleElements = Array.from(form.querySelectorAll(RULE_SELECTOR));

    if (!passwordInput || !submitButton || ruleElements.length === 0) {
        return null;
    }

    const state = {
        advisory: form.dataset.passwordValidationMode === 'advisory',
        confirmationInput,
        eventController: new AbortController(),
        form,
        optional: form.hasAttribute('data-password-optional'),
        passwordInput,
        popoverContent: form.querySelector(POPOVER_CONTENT_SELECTOR),
        ruleElements,
        statusElement: form.querySelector(STATUS_SELECTOR),
        submitButton,
    };

    form[STATE] = state;
    form.dataset.passwordValidationBound = '1';

    initializePopover(state);

    if (state.optional && passwordInput.value.length === 0) {
        renderEmpty(state);
    } else {
        ruleElements.forEach(ruleElement => setIconVisibility(ruleElement, false));
    }

    // Every check is a rate limited request, so typing, blur and paste in both fields share one debounced check
    const debouncedUpdate = debounce(() => updatePasswordValidation(form), 400);
    const handleInput = () => {
        if (state.optional && passwordInput.value.length === 0) {
            updatePasswordValidation(form);

            return;
        }

        if (form.dataset.passwordValidationError === 'inline') {
            setSubmitDisabled(state, true);
        }

        debouncedUpdate();
    };

    const listenerOptions = {
        signal: state.eventController.signal,
    };

    passwordInput.addEventListener('input', handleInput, listenerOptions);
    passwordInput.addEventListener('blur', debouncedUpdate, listenerOptions);
    passwordInput.addEventListener('paste', debouncedUpdate, listenerOptions);

    if (confirmationInput) {
        confirmationInput.addEventListener('input', handleInput, listenerOptions);
        confirmationInput.addEventListener('blur', debouncedUpdate, listenerOptions);
        confirmationInput.addEventListener('paste', debouncedUpdate, listenerOptions);
    }

    updatePasswordValidation(form);

    return state;
};

const newPassword = (container = document) => {
    return getForms(container)
        .map(bindPasswordValidation)
        .filter(Boolean);
};

const applyGeneratedPassword = (container, password) => {
    if (!container || typeof password !== 'string') {
        return;
    }

    const [state] = newPassword(container);

    if (!state) {
        return;
    }

    // The Livewire action already owns this generated value. Only restore it to the real
    // password control after morphing; dispatching a second model update causes another
    // morph that clears browser-managed password input values.
    state.passwordInput.value = password;

    updatePasswordValidation(state.form);

    const generateButton = state.focusRegion?.querySelector(GENERATE_SELECTOR);
    (generateButton || state.passwordInput).focus({ preventScroll: true });
    state.showPopover?.();
};

export { applyGeneratedPassword, bindPasswordValidation, updatePasswordValidation, newPassword };
export default newPassword;
